import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import { CvDetailObject } from '../interface/CvDetailInterface';

export const ProfileSkills = (
  {
    cvDetail,
    keyword = '',
  }:
  { cvDetail: CvDetailObject ;
    keyword: string },
) => {
  const { t } = useTranslation('common');
  const [skillsToShow, setSkillsToShow] = useState<number>(5);
  const [hasAlreadySeenAll, setHasAlreadySeenAll] = useState<boolean>(false);
  const [collapsedShowMore, setCollapsedShowMore] = useState<boolean>(false);

  // ESCO skills were removed from the CV edit form, so they are no longer shown
  // to anyone (employers or the candidate on their own CV). This array still
  // carries spoken languages via the isLanguage flag, so keep only those.
  const allSkills = (cvDetail?.skillsWithHighlight || []) as { name: string; highlight: boolean; isLanguage: boolean }[];
  const visibleSkills = allSkills.filter((item) => item?.isLanguage);
  // Section holds only languages now, so label it accordingly.
  const sectionLabel = t('cv.section.label.spoken_languages');

  const showMoreSkills = () => {
    setSkillsToShow(visibleSkills.length);
    setCollapsedShowMore(true);
    setHasAlreadySeenAll(true);
  };

  const showLessSkills = () => {
    setSkillsToShow(5);
    setCollapsedShowMore(false);
    setHasAlreadySeenAll(true);
  };

  // show all the skills if the keyword is not in (skillsToShow state)
  if (visibleSkills.length && !hasAlreadySeenAll) {
    if (visibleSkills.map((item) => item?.name).indexOf(keyword) >= skillsToShow) {
      showMoreSkills();
    }
  }

  const skillClass = 'flex items-center text-sm text-ink py-1 px-3 rounded-full mr-2 cursor-pointer border border-surface-100 hover:bg-surface mb-2';

  return (
    <>
      {visibleSkills.length > 0
            && (
            <section className="pt-2">
              <h6 className="mb-4 mr-2 border-b border-dotted pb-4 text-lg font-semibold">
                {`${sectionLabel} (${visibleSkills.length})`}
              </h6>
              {/* check if skill is the keyword and highlight it */}
              <div className="flex flex-wrap items-center justify-start">
                {visibleSkills.slice(0, skillsToShow).map((item) => (
                  <div
                    key={item.name}
                    className={`${skillClass}${item.isLanguage ? ' bg-transparent' : ''}${keyword === item.name ? ' bg-secondary/20' : ' bg-surface-100'}`}
                  >
                    {item.highlight
                        && <CheckIcon className="mr-2 size-5 text-secondary" />}
                    {item.name}
                  </div>
                ))}
              </div>

              {!collapsedShowMore && visibleSkills.length > 5
                  && (
                  <div className="my-2 flex cursor-pointer items-center" role="presentation" onClick={showMoreSkills}>
                    <span className="mr-2 block text-sm text-primary">
                      {`${t('global.btn.see_more')} `}
                    </span>
                    <ChevronDownIcon className="inline-block size-5 text-primary" />
                  </div>
                  )}

              {collapsedShowMore
                  && (
                  <div className="my-2 flex cursor-pointer items-center" role="presentation" onClick={showLessSkills}>
                    <span className="mr-2 block text-sm text-primary">
                      {t('global.btn.see.less')}
                    </span>
                    <ChevronUpIcon className="inline-block size-5 text-primary" />
                  </div>
                  )}
            </section>
            )}
    </>
  );
};
